<!-- A page to assign a TelevisionType to a TVDecoder.

This assignment is relevant for the TelevisionRemote feature, which
allows the user to control content and display size via a web based
frontend.

-->
<template>
  <div>
    <navbar :links="['dashboard', $route['name']]" />
    <!-- Loading Indicator -->
    <div
      v-if="loading"
      class="loading-indicator mt-5"
    >
      <v-progress-circular
        :size="50"
        indeterminate
        class="msr--text"
      />
    </div>
    <v-alert
      v-if="!loading && !workspaceHasTelevisionRealm"
      class="mt-4"
      color="warning"
      :value="true"
    >
      {{ $t('televisionRealmMissingOnUserWorkspace') }}.
    </v-alert>
    <!-- TVDecoder to TelevisionType Assignments -->
    <v-data-table
      v-if="!loading && workspaceHasTelevisionRealm"
      hide-actions
      disable-initial-sort
      :headers="headers"
      :items="tvdecoders"
      class="mt-4"
      :no-data-text="$t('noDataText')"
      :no-results-text="$t('noResultsText')"
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <!-- TVDecoder: Store -->
        <td>
          <v-layout
            class="body-1 py-2"
            align-center
          >
            <v-icon
              medium
              class="msr--text mr-3 unselectable"
            >
              location_on
            </v-icon>
            <div>
              <small class="d-block grey--text text--darken-2">{{
                props.item.store.name
              }}</small>
              <strong>{{ props.item.store.city }}</strong>
            </div>
          </v-layout>
        </td>
        <!-- TVDecoder: Icon + Description -->
        <td>
          <v-layout align-center>
            <icon-tv-decoder
              :tvdecoder="props.item"
              class="mr-2"
            />
            {{ props.item.description }}
          </v-layout>
        </td>
        <!-- TelevisionType: Article No., Product Family, Label -->
        <td
          style="position: relative; max-width: 480px"
          class="pseudo-anchor--inverted"
          :class="{'warning--text': !props.item.television_type}"
          :title="$t('televisionTypeAssign')"
          @click="openAssignmentDialog(props.item)"
        >
          <div
            v-if="props.item.television_type"
            class="my-2"
          >
            <span
              v-if="props.item.television_type.product_family"
            ><strong>{{ props.item.television_type.product_family }}</strong>,
            </span>
            {{ props.item.television_type.label }}
            <div
              class="grey--text text--darken-2 mt-1"
              style="font-size: 10px"
            >
              <div class="mt-2">
                {{ props.item.television_type.article_number }}
              </div>
            </div>
          </div>
          <div v-else>
            {{ $t('televisionTypeNotAssignedYet') }}
          </div>
        </td>
        <!-- TelevisionType: Screen Diagonal -->
        <td>
          <div
            v-if="
              props.item.television_type &&
                props.item.television_type.screen_diagonal
            "
            class="headline grey--text text--darken-3"
          >
            {{ props.item.television_type.screen_diagonal }}"
          </div>
        </td>
        <!-- TelevisionType: Brand -->
        <td>
          <span
            v-if="
              props.item.television_type &&
                props.item.television_type.brand_logo_thumbnail_url
            "
          >
            <img
              :src="props.item.television_type.brand_logo_thumbnail_url"
              width="64"
              class="my-2"
              style="height: auto; max-height: 64px; object-fit: contain"
            >
          </span>
        </td>
        <!-- TelevisionType: EEK -->
        <td>
          <span
            v-if="props.item.television_type && props.item.television_type.eek"
          >
            <img
              :src="eekToImageUrl[props.item.television_type.eek]"
              width="32"
              class="my-2"
              :title="props.item.television_type.eek"
              style="height: auto; max-height: 32px; object-fit: contain"
            >
          </span>
        </td>
        <!-- TelevisionType: Image -->
        <td>
          <span
            v-if="
              props.item.television_type &&
                props.item.television_type.product_thumbnail_image_url
            "
          >
            <img
              :src="props.item.television_type.product_thumbnail_image_url"
              width="96"
              class="my-2"
              style="height: auto; max-height: 96px; object-fit: contain"
            >
          </span>
        </td>
        <td>
          <v-menu lazy>
            <v-btn
              slot="activator"
              icon
              class="ma-0"
            >
              <v-icon>more_vert</v-icon>
            </v-btn>
            <v-list>
              <!-- Add/change Assignment -->
              <v-list-tile @click="openAssignmentDialog(props.item)">
                <v-list-tile-avatar>
                  <v-icon v-if="!!props.item.television_type">
                    edit
                  </v-icon>
                  <v-icon v-else>
                    add
                  </v-icon>
                </v-list-tile-avatar>
                <v-list-tile-title>
                  {{
                    !!props.item.television_type
                      ? $t('televisionTypeChangeAssignment')
                      : $t('televisionTypeAssign')
                  }}
                </v-list-tile-title>
              </v-list-tile>
              <v-divider v-if="props.item.television_type != null" />
              <!-- Remove Assignment -->
              <v-list-tile
                v-if="props.item.television_type != null"
                @click="confirmRemoveAssignment(props.item)"
              >
                <v-list-tile-avatar>
                  <v-icon>delete</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-title>
                  {{ $t('televisionTypeRemoveAssignment') }}
                </v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
        </td>
      </template>
    </v-data-table>
    <!-- Assignment Dialog -->
    <television-type-assignment-dialog
      :show.sync="showAssignmentDialog"
      :loading="loading"
      :tvdecoder="tvdecoderToEdit"
      :television-types-page="televisionTypesPage"
      :television-types-page-index.sync="televisionTypesPageIndex"
      :television-type-ids-with-stock="televisionTypeIdsWithStock"
      :search-text.sync="televisionTypesSearchText"
      :filter-by-screen-diagonal.sync="televisionTypesFilterByScreenDiagonal"
      :filter-by-store.sync="televisionTypesFilterByStore"
      @save="updateTelevisionType"
    />
  </div>
</template>

<script>
import debounce from 'lodash.debounce';

import {removeUrlQueryParamsFromCurrentRoute} from '@/utils';

import IconTvDecoder from '@/components/IconTvDecoder';
import Navbar from '@/components/Navbar';
import TelevisionTypeAssignmentDialog from '@/components/TelevisionTypeAssignmentDialog';

export const EEK_TO_IMAGE_URL = {
  A: 'https://cms.myshopradio.de/filer/canonical/1724069773/1271/',
  B: 'https://cms.myshopradio.de/filer/canonical/1724069768/1265/',
  C: 'https://cms.myshopradio.de/filer/canonical/1724069768/1267/',
  D: 'https://cms.myshopradio.de/filer/canonical/1724069768/1266/',
  E: 'https://cms.myshopradio.de/filer/canonical/1724069770/1268/',
  F: 'https://cms.myshopradio.de/filer/canonical/1724069771/1269/',
  G: 'https://cms.myshopradio.de/filer/canonical/1724069771/1270/',
};

export default {
  components: {
    IconTvDecoder,
    Navbar,
    TelevisionTypeAssignmentDialog,
  },
  data() {
    return {
      loading: false,
      updating: false,

      showAssignmentDialog: false,
      tvdecoderToEdit: null,
      televisionTypeIdsWithStock: [],

      televisionTypesPageIndex: 1,
      televisionTypesSearchText: '',
      televisionTypesFilterByScreenDiagonal: null,
      televisionTypesFilterByStore: null,

      // From backend
      tvdecoders: [],
      televisionTypesPage: null,
    };
  },
  computed: {
    eekToImageUrl() {
      return EEK_TO_IMAGE_URL;
    },
    workspaceHasTelevisionRealm() {
      return (
        !!this.$userprofile &&
        !!this.$userprofile.workspace &&
        !!this.$userprofile.workspace.television_realm
      );
    },
    headers() {
      return [
        {
          text: this.$t('store'),
          value: 'store.city',
          sortable: true,
        },
        {
          text: this.$t('decoder'),
          value: 'description',
          sortable: true,
        },
        // Next columns belong together, so first gets a label while the
        // others don't. We use multiple columns since some fields are
        // optional, but we always want a fixed/aligned layout even if
        // some fields are empty.
        {
          text: this.$t('televisionType'),
          value: 'television_type.label',
          sortable: true,
        },
        {
          text: '',
          value: 'television_type.screen_diagonal',
          sortable: false,
        },
        {
          text: '',
          value: 'television_type.brand_logo_thumbnail_url',
          sortable: false,
        },
        {
          text: '',
          value: 'television_type.product_thumbnail_image_url',
          sortable: false,
        },
        {
          text: '',
          value: 'extra-buttons',
          sortable: false,
        },
      ];
    },
  },
  watch: {
    tvdecoderToEdit: function (tvdecoder) {
      this.updateStocksForTVDecoder(tvdecoder);
    },
    televisionTypesPageIndex: function () {
      this.fetchTelevisionTypes({indicator: true});
    },
    televisionTypesSearchText: function () {
      this.resetTelevisionTypesPageIndex();
      this.debouncedFetchTelevisionTypes({indicator: true});
    },
    televisionTypesFilterByScreenDiagonal: function () {
      this.resetTelevisionTypesPageIndex();
      this.fetchTelevisionTypes({indicator: true});
    },
    televisionTypesFilterByStore: function () {
      this.resetTelevisionTypesPageIndex();
      this.fetchTelevisionTypes({indicator: true});
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    updateStocksForTVDecoder(tvdecoder) {
      if (!tvdecoder) {
        this.televisionTypeIdsWithStock = [];
      } else {
        this.$api
          .listTelevisionTypeIdsWithStockForStore(tvdecoder.store.id)
          .then((response) => {
            this.televisionTypeIdsWithStock = response.data;
          });
      }
    },
    resetTelevisionTypesPageIndex() {
      this.televisionTypesPageIndex = 1;
    },
    confirmRemoveAssignment(tvdecoder) {
      const confirmed = confirm(
        this.$t('televisionTypeConfirmRemoveAssignment')
      );
      if (!confirmed) {
        return;
      }

      this.tvdecoderToEdit = tvdecoder;
      this.updateTelevisionType(null);
    },
    openAssignmentDialog(tvdecoder) {
      if (this.updating) {
        return;
      }
      this.tvdecoderToEdit = tvdecoder;
      this.showAssignmentDialog = true;
    },
    updateTelevisionType(televisionTypeOrNull) {
      if (!this.tvdecoderToEdit) {
        return;
      }

      // Frontend update
      this.tvdecoderToEdit.television_type = televisionTypeOrNull;

      const televisionTypeValue =
        televisionTypeOrNull != null ? televisionTypeOrNull.id : null;

      // Backend update
      this.updating = true;
      this.$api
        .assignTelevisionTypeToTVDecoder(
          televisionTypeValue,
          this.tvdecoderToEdit.id
        )
        .then(() => {
          this.$bus.$emit('alert-success', {
            message: this.$t('genericEntryUpdateSuccess'),
            timeout: 1000,
          });
        })
        .finally(() => {
          this.updating = false;
          this.tvdecoderToEdit = null;
        });
    },
    fetchData() {
      this.loading = true;
      return Promise.all([
        this.$store.dispatch('MAYBE_RETRIEVE_CURRENT_USER'),
        this.fetchTVDecodersForAssignment(),
        this.fetchTelevisionTypes(),
      ]).finally(() => {
        this.loading = false;
      });
    },
    fetchTVDecodersForAssignment() {
      return this.$api.listTVDecodersForTVAssignment().then((response) => {
        this.tvdecoders = response.data;
        this.handleOptionalPreselectQuery();
      });
    },
    fetchTelevisionTypes({indicator = false} = {}) {
      if (indicator) {
        this.loading = true;
      }
      return this.$api
        .listTelevisionTypes(
          this.televisionTypesPageIndex,
          this.televisionTypesSearchText,
          this.televisionTypesFilterByScreenDiagonal,
          this.televisionTypesFilterByStore
        )
        .then((response) => {
          this.televisionTypesPage = response.data;
          if (indicator) {
            this.loading = false;
          }
        });
    },
    debouncedFetchTelevisionTypes: debounce(function () {
      this.fetchTelevisionTypes({indicator: true});
    }, 250),
    handleOptionalPreselectQuery() {
      const preselectTVDecoderId = this.$route.query.preselect;
      if (!preselectTVDecoderId) {
        return;
      }

      const tvdecoder = this.tvdecoders.find(
        (tvdecoder) => tvdecoder.id == preselectTVDecoderId
      );

      if (!tvdecoder) {
        this.$bus.$emit('alert-warning', {
          message: this.$t('televisionTypeAssignPreselectTVDecoderNotFound'),
          timeout: 15000,
        });
        return;
      }

      this.openAssignmentDialog(tvdecoder);
      removeUrlQueryParamsFromCurrentRoute();
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/TelevisionTypeAssignment.vue